import { useEffect, useMemo, useState } from "react";
import ExactTransactionContributorRow from "../components/ExactTransactionContributorRow";
import { useTranslation } from "react-i18next";
import "./ExactTransactionContributorList.css";
import { ICONS } from "../constants";
import { amountDataToDisplay, amountInputToData, getStableAnimalEmoji } from "../utils/utils";

function buildInitialSelected(contributors) {
  const selected = {};
  (contributors || []).forEach((c) => {
    selected[c.id] = true;
  });
  return selected;
}

function centsFromInput(value) {
  const s = `${value ?? ""}`.trim();
  if (s === "") return 0;
  const n = parseFloat(s);
  if (isNaN(n)) return 0;
  return amountInputToData(s);
}

function buildInitialDraftAmountInputs(contributors, contributorAmountList) {
  const draft = {};
  (contributors || []).forEach((c) => {
    const cents = contributorAmountList?.[c.id]?.amount ?? 0;
    draft[c.id] = amountDataToDisplay(cents);
  });
  return draft;
}

export default function ExactTransactionContributorList(props) {
  const { t } = useTranslation();
  const {
    contributors,
    amount,
    contributorAmountList,
    setContributorAmountList,
    contributorAmountIsEditing,
    setContributorAmountIsEditing,
    amountSyncVersion,
    onAdvancedConfirm,
    requestEditMode,
    transaction,
    newTransaction,
    shouldReloadRow,
    editMode
  } = props;
  const [dividedAmountList, setDividedAmountList] = useState([]);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showAdvancedInfo, setShowAdvancedInfo] = useState(false);

  // Advanced modal state (inlined; no separate component file)
  const heroEmoji = useMemo(() => {
    const seed = `exact-advanced:${(contributors || []).map((c) => c.id).join(",")}`;
    return getStableAnimalEmoji(seed);
  }, [contributors]);
  const [advancedSelected, setAdvancedSelected] = useState(() =>
    buildInitialSelected(contributors)
  );
  const [advancedDraftAmountInputs, setAdvancedDraftAmountInputs] = useState(() =>
    buildInitialDraftAmountInputs(contributors, contributorAmountList)
  );
  const totalCents = useMemo(() => {
    const n = parseFloat(amount);
    if (isNaN(n)) return 0;
    return amountInputToData(`${n}`);
  }, [amount]);

  // Re-init advanced modal every time it opens (so it reflects latest list values)
  useEffect(() => {
    if (!showAdvanced) return;
    setShowAdvancedInfo(false);
    setAdvancedSelected(buildInitialSelected(contributors));
    setAdvancedDraftAmountInputs(
      buildInitialDraftAmountInputs(contributors, contributorAmountList)
    );
  }, [showAdvanced, contributors, contributorAmountList]);

  const selectedIds = useMemo(() => {
    return Object.entries(advancedSelected)
      .filter(([, v]) => !!v)
      .map(([k]) => k);
  }, [advancedSelected]);

  const unselectedIds = useMemo(() => {
    return Object.entries(advancedSelected)
      .filter(([, v]) => !v)
      .map(([k]) => k);
  }, [advancedSelected]);

  const selectedTotalCents = useMemo(() => {
    return selectedIds.reduce(
      (acc, id) => acc + centsFromInput(advancedDraftAmountInputs[id]),
      0
    );
  }, [selectedIds, advancedDraftAmountInputs]);

  const restCents = useMemo(() => {
    return totalCents - selectedTotalCents;
  }, [totalCents, selectedTotalCents]);

  const handleAdvancedClearAll = () => {
    setAdvancedDraftAmountInputs((prev) => {
      const next = { ...prev };
      selectedIds.forEach((id) => {
        next[id] = "0";
      });
      return next;
    });
  };

  const handleAdvancedEvenTheRest = () => {
    const n = unselectedIds.length;
    if (n <= 0) return;

    const base = Math.trunc(restCents / n); // cents
    const remainder = restCents - base * n;

    setAdvancedDraftAmountInputs((prev) => {
      const next = { ...prev };
      unselectedIds.forEach((id, idx) => {
        const cents = base + (idx === n - 1 ? remainder : 0);
        next[id] = amountDataToDisplay(cents);
      });
      return next;
    });
  };

  const handleAdvancedConfirm = () => {
    const payload = {};
    (contributors || []).forEach((c) => {
      payload[c.id] = { amount: centsFromInput(advancedDraftAmountInputs[c.id]), share: 0 };
    });
    setContributorAmountIsEditing(true);
    setContributorAmountList(payload);
    onAdvancedConfirm?.();
    setShowAdvanced(false);
  };

  useEffect(() => {
    const divideAmount = (amount, shares) => {
      const result = [];

      for (let i = 0; i < shares - 1; i++) {
        result.push(+(amount / shares).toFixed(2));
      }
      result.push(+(amount - result.reduce((a, b) => a + b, 0)).toFixed(2));

      return result;
    };

    const shares = (contributors && contributors.length) || 1;
    setDividedAmountList(divideAmount(amount, shares));
  }, [amount, contributors]);

  return (
    <>
      <div className="exact-amounts-toolbar">
        <div className="exact-amounts-toolbar-divider">
          <span className="exact-amounts-toolbar-label">
            {t("transaction.exact_amounts_advanced.tools_label")}
          </span>
        </div>
        <div className="d-flex justify-content-end align-items-center gap-2">
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => {
              if (!editMode) requestEditMode?.();
              setShowAdvanced(true);
            }}
          >
            {t("transaction.exact_amounts_advanced.button")}
          </button>
        </div>
      </div>

      <ul className="list-group list-group-flush">
      {contributors &&
        contributors.map((c, i) => (
          <li className="list-group-item" key={c.id}>
            <ExactTransactionContributorRow
              contributor={c}
              dividedAmount={dividedAmountList[i]}
              externalAmountCents={contributorAmountList?.[c.id]?.amount}
              amountSyncVersion={amountSyncVersion}
              setContributorAmountList={setContributorAmountList}
              contributorAmountIsEditing={contributorAmountIsEditing}
              setContributorAmountIsEditing={setContributorAmountIsEditing}
              newTransaction={newTransaction}
              transaction={transaction}
              shouldReloadRow={shouldReloadRow}
              editMode={editMode}
            />
          </li>
        ))}
      </ul>

      {showAdvanced && (
        <div
          className="exact-amounts-advanced-backdrop"
          role="dialog"
          aria-modal="true"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setShowAdvanced(false);
          }}
        >
          <div className="exact-amounts-advanced-modal card">
            <div className="card-header d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center gap-2">
                <div className="exact-amounts-advanced-hero-emoji" aria-hidden="true">
                  {heroEmoji}
                </div>
                <div>
                  <div className="fw-semibold">
                    {t("transaction.exact_amounts_advanced.title")}
                  </div>
                </div>
              </div>
              <div className="d-flex align-items-center gap-2">
                <button
                  type="button"
                  className={`btn btn-sm ${showAdvancedInfo ? "btn-light" : "btn-outline-light"}`}
                  onClick={() => setShowAdvancedInfo((v) => !v)}
                  aria-label={t("transaction.exact_amounts_advanced.info_button_aria")}
                  aria-expanded={showAdvancedInfo}
                  aria-controls="exact-amounts-advanced-info"
                >
                  <i className="bi bi-info-circle" />
                </button>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowAdvanced(false)}
                  aria-label="Close"
                />
              </div>
            </div>

            <div className="card-body">
              {showAdvancedInfo && (
                <div
                  id="exact-amounts-advanced-info"
                  className="exact-amounts-info-panel small mt-0 mb-2"
                >
                  <div className="text-muted mb-2">
                    {t("transaction.exact_amounts_advanced.subtitle")}
                  </div>
                  <div className="fw-semibold mb-1">
                    {t("transaction.exact_amounts_advanced.info_title")}
                  </div>
                  <div className="mb-2">{t("transaction.exact_amounts_advanced.info_body")}</div>
                  <div className="text-muted">{t("transaction.exact_amounts_advanced.hint")}</div>
                </div>
              )}

              <div className="exact-amounts-advanced-summary small mb-2">
                <div>
                  <span className="text-muted">{t("transaction.amount")}:</span>{" "}
                  <span className="fw-semibold">${amountDataToDisplay(totalCents)}</span>
                </div>
                <div>
                  <span className="text-muted">
                    {t("transaction.exact_amounts_advanced.selected_total")}:
                  </span>{" "}
                  <span className="fw-semibold">${amountDataToDisplay(selectedTotalCents)}</span>
                </div>
                <div>
                  <span className="text-muted">
                    {t("transaction.exact_amounts_advanced.rest")}:
                  </span>{" "}
                  <span className="fw-semibold">${amountDataToDisplay(restCents)}</span>
                </div>
              </div>

              <div className="exact-amounts-advanced-list">
                {(contributors || []).map((c) => (
                  <label
                    key={c.id}
                    className={`exact-amounts-advanced-item d-flex align-items-center justify-content-between ${
                      advancedSelected[c.id] ? "is-selected" : "is-unselected"
                    }`}
                  >
                    <div className="d-flex align-items-center gap-2 text-truncate">
                      <input
                        className="form-check-input m-0"
                        type="checkbox"
                        checked={!!advancedSelected[c.id]}
                        onChange={(e) =>
                          setAdvancedSelected((prev) => ({
                            ...prev,
                            [c.id]: e.target.checked,
                          }))
                        }
                      />
                      <div className="fs-5">{ICONS[c.icon]}</div>
                      <div className="text-truncate">{c.name}</div>
                    </div>
                    <div className="ms-2 d-flex align-items-center gap-1">
                      <span className="text-muted">$</span>
                      <input
                        type="number"
                        step="0.01"
                        className="form-control form-control-sm exact-amounts-advanced-amount-input"
                        value={advancedDraftAmountInputs[c.id] ?? ""}
                        onChange={(e) =>
                          setAdvancedDraftAmountInputs((prev) => ({
                            ...prev,
                            [c.id]: e.target.value,
                          }))
                        }
                      />
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div className="card-footer d-flex justify-content-end gap-2 exact-amounts-advanced-footer">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={handleAdvancedClearAll}
              >
                {t("transaction.exact_amounts_advanced.clear_all")}
              </button>
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={handleAdvancedEvenTheRest}
              >
                {t("transaction.exact_amounts_advanced.even_the_rest")}
              </button>
              <button type="button" className="btn btn-primary" onClick={handleAdvancedConfirm}>
                {t("transaction.exact_amounts_advanced.confirm")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
